import { UploadOutlined } from "@ant-design/icons";
import { Button, Input, message, Modal, Upload } from "antd";
import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { saveSinistreFile, getDecaissementByCodeId, getDecaissementsByGroupe } from "../../../hooks/useHooks";
import axiosInstance from "../../../variables/api/axiosInstance";

const { Dragger } = Upload

// Opération LONGUE (remise/encaissement d'un lot) : le proxy peut renvoyer 502/504 alors que la
// transaction backend a ABOUTI. On distingue ce cas d'un vrai échec applicatif (4xx avec corps).
const estTimeoutPasserelle = (err) => {
    const s = err?.response?.status;
    return !err?.response || s === 502 || s === 503 || s === 504 || err?.code === "ECONNABORTED";
};
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
// La remise est appliquée quand la/les ligne(s) concernée(s) ne sont plus EN_ATTENTE_D_ENCAISSEMENT.
const remiseAppliquee = async (decaissement) => {
    if (decaissement?.groupeReglementCodeId) {
        const res = await getDecaissementsByGroupe(decaissement.groupeReglementCodeId);
        const lignes = res?.data || [];
        if (!lignes.length) return false;
        return !lignes.some((x) => (x?.decaissement?.etatDecaissement ?? x?.etatDecaissement) === "EN_ATTENTE_D_ENCAISSEMENT");
    }
    const res = await getDecaissementByCodeId(decaissement?.codeId);
    const etat = res?.data?.etatDecaissement ?? res?.data?.decaissement?.etatDecaissement;
    return !!etat && etat !== "EN_ATTENTE_D_ENCAISSEMENT";
};
const attendreRemise = async (decaissement, { tries = 8, delayMs = 3000 } = {}) => {
    for (let k = 0; k < tries; k++) {
        await sleep(delayMs);
        try { if (await remiseAppliquee(decaissement)) return true; } catch (_) { /* on retente */ }
    }
    return false;
};

const RemiseCheque = (prop) => {
    const [modalDecaissementSinistre, setModalDecaissementSinistre] = useState(
        false
    );
    const [cheque, setCheque] = useState("");
    const [cni, setCni] = useState("");
    const [loading, setLoading] = useState(false);
    const [numeroCni, setNumeroCni] = useState("");
    const [representant, setRepresentant] = useState("");

    // Règlement par VIREMENT : on demande l'ORDRE DE VIREMENT et le DOCUMENT DE CONSTATATION DE
    // DÉBIT DU COMPTE (au lieu du chèque et de la CNI), et le N° de CNI n'est plus exigé.
    const isVirement = prop?.decaissement?.modePaiement === "VIREMENT";

    const handleDecaissementSubmit = async () => {
        setLoading(true);
        const decaissement = prop?.decaissement;
        if (!decaissement) {
            message.error("Erreur lors de la remise");
            setLoading(false);
            return;
        }

        // Pour un chèque, le N° de CNI (bénéficiaire/représentant qui endosse) reste obligatoire.
        // Pour un virement, il n'y a pas d'endossement → CNI non exigée.
        if (!isVirement && (!numeroCni || numeroCni.trim() === "")) {
            message.error("Numero de CNI obligatoire");
            setLoading(false);
            return;
        }

        const remiseDTO = {
            cni: numeroCni ? numeroCni.trim() : null,
            representant: representant ? representant.trim() : null,
            recepteurCodeId: prop?.recepteur?.codeId || null
            // piecesJustificatives: [ ... ] // inactif cote backend pour le moment
        };

        try {
            const res = await axiosInstance.put(
                `/api/decaissement/remise/${decaissement.codeId}`,
                JSON.stringify(remiseDTO),
                { headers: { 'Content-Type': 'application/json' } }
            );
            message.success(isVirement ? "Encaissement du virement effectué avec succès" : "Remise de chèque effectuée avec succès");
            setModalDecaissementSinistre(false);
            setNumeroCni("");
            setRepresentant("");
            if (prop.onSuccess) prop.onSuccess(res.data);
            else window.location.reload();
        } catch (err) {
            console.log(err);
            // Timeout passerelle (502/504) sur une remise longue : la transaction a probablement
            // abouti. On vérifie l'état réel avant d'annoncer une erreur.
            if (estTimeoutPasserelle(err)) {
                message.info("Remise longue en cours — vérification de l'état…");
                const ok = await attendreRemise(decaissement);
                if (ok) {
                    message.success(isVirement ? "Encaissement du virement effectué avec succès" : "Remise de chèque effectuée avec succès");
                    setModalDecaissementSinistre(false);
                    setNumeroCni("");
                    setRepresentant("");
                    if (prop.onSuccess) prop.onSuccess();
                    else window.location.reload();
                } else {
                    message.warning("La remise est peut-être encore en cours. Actualisez dans un instant.");
                }
                return;
            }
            const msg = err?.response?.data?.message || "Erreur lors de la remise du chèque";
            message.error(msg);
        } finally {
            setLoading(false);
        }
    };

    const uploadSinistreFile = async (file, onSuccess, onError, onProgress) => {
        try {

            const response = await saveSinistreFile(file);
            console.log(response.data)
            //  await formik.setFieldValue('croquis', sessionStorage.getItem('file'))
            message.success(`${file.name} téléversé avec succès.`);
            return response
        } catch (error) {
            // Handle errors
            onError(error);
            message.error(`Échec de l'upload du fichier: ${file.name}`);
        }
    }

    const props = {
        name: "cheque",
        customRequest: async (options) => {
            const { file, onSuccess, onError, onProgress } = options;
            const resCheque = await uploadSinistreFile(file, onSuccess, onError, onProgress)
            setCheque(resCheque);
            console.log('cni cheque ', cheque);

        },
        onChange(info) {
            if (info.file.status !== "uploading") {
                console.log(info.file, info.fileList);
            }
            if (info.file.status === "done") {
                setCheque(info.file.originFileObj);
                toast.success(`${info.file.name} fichier téléversé avec succès`);
            } else if (info.file.status === "error") {
                toast.error(
                    `${info.file.name} erreur lors du téléversement du fichier.`
                );
            }
        },
        progress: {
            strokeColor: {
                "0%": "#108ee9",
                "100%": "#87d068",
            },
            strokeWidth: 3,
            format: (percent) => percent && `${parseFloat(percent.toFixed(2))}%`,
        },
    };
    const getFrenchDate = () => {
        const now = dayjs();
        return now.format("DD-MM-YYYY");
    };
    const props2 = {
        name: "cni",
        customRequest: async (options) => {
            const { file, onSuccess, onError, onProgress } = options;
            const resCni = await uploadSinistreFile(file, onSuccess, onError, onProgress)
            console.log('cni r', resCni)
            setCni(resCni)
            console.log('cni 2 ', cni)
        },
        onChange(info) {
            if (info.file.status !== "uploading") {
                console.log(info.file, info.fileList);
            }
            if (info.file.status === "done") {
                setCni(info.file.originFileObj);
                console.log('cni ', cni)
                message.success(`${info.file.name} fichier téléversé avec succès`);
            } else if (info.file.status === "error") {
                message.error(
                    `${info.file.name} erreur lors du téléversement du fichier.`
                );
            }
        },
        progress: {
            strokeColor: {
                "0%": "#108ee9",
                "100%": "#87d068",
            },
            strokeWidth: 3,
            format: (percent) => percent && `${parseFloat(percent.toFixed(2))}%`,
        },
    };

    useEffect(() => {
        console.log(prop.decaissement);
        setModalDecaissementSinistre(prop.open);
    }, [prop.open]);

    // useEffect(() => {
    //   prop.setOpen(modalDecaissementSinistre);
    // }, [modalDecaissementSinistre]);

    return (
        <Modal
            title={isVirement ? "Encaissement du virement" : "Remise de chèque"}
            confirmLoading={loading}
            centered
            open={modalDecaissementSinistre}
            onOk={() => handleDecaissementSubmit()}
            onCancel={prop.onCancel}
            okText={"Effectuer"}
            cancelText={"Annuler"}
            width={700}
        >
            <div className="row">

                <br />
                <div className="col-12" style={{ justifyContent: 'center', marginTop: '5%' }}>
                    <Dragger {...props}>
                        <Button icon={<UploadOutlined />}>
                            {isVirement ? "Joindre l'ordre de virement" : "Joindre le fichier du chèque"}
                        </Button>
                    </Dragger>
                </div>
                <br />
                <div className="col-12" style={{ justifyContent: 'center', marginTop: '5%' }}>
                    <Dragger {...props2}>
                        <Button icon={<UploadOutlined />}>
                            {isVirement
                                ? "Joindre le document de constatation de débit du compte"
                                : "Joindre la CNI endossant le chèque"}
                        </Button>
                    </Dragger>
                </div>
                {/* Champs propres au chèque (CNI + représentant qui endosse). Sans objet pour un virement. */}
                {!isVirement && (
                    <>
                        <br />
                        <div className="col-12" style={{ justifyContent: 'center', marginTop: '5%' }}>
                            <Input
                                placeholder="N° de CNI"
                                value={numeroCni}
                                onChange={(e) => setNumeroCni(e.target.value)}
                            />
                        </div>
                        <br />
                        <div className="col-12" style={{ justifyContent: 'center', marginTop: '5%' }}>
                            <Input
                                placeholder="Nom du représentant"
                                value={representant}
                                onChange={(e) => setRepresentant(e.target.value)}
                            />
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
};

RemiseCheque.propTypes = {
    open: PropTypes.bool,
    // setOpen: PropTypes.func,
    decaissement: PropTypes.object,
    sinistre: PropTypes.object,
};

export default RemiseCheque;
